import type { Connection } from '@solana/web3.js';
import { fetchGraduatedPoolInfo } from '../accounts/fetch.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import { CP_SWAP_PROGRAM_ID } from '../constants/programs.js';
import { deriveAmmConfigPda } from '../pda/cp-swap.js';
import { deriveOutcomeMintPda } from '../pda/derivations.js';
import type { BuildResult } from '../types/build-result.js';
import type { AmmSwapParams } from '../types/trading.js';
import {
  buildAmmSwapInstruction,
  resolveAmmSwapRoute,
  validateAmmSwapAmounts,
} from './amm-swap-instruction.js';
import { createBuildResult, createIdempotentAtaIx, uniqueSignerKeys } from './common.js';

/**
 * Build a swap on a graduated outcome's cp-swap pool (Raydium CPMM fork) via
 * `swap_base_input`. This runs entirely in the cp-swap program — the markets
 * program is never called. The only fee is the pool's swap fee, taken from the
 * input by the on-chain math.
 *
 * `direction: 'buy'` swaps USDC→outcome (`amountIn` = USDC, `minAmountOut` =
 * min outcome tokens); `direction: 'sell'` swaps outcome→USDC (`amountIn` =
 * outcome tokens, `minAmountOut` = min USDC). All amounts are D6 base units.
 *
 * Pool identity is the canonical AmmConfig index-1 PDA plus the outcome mint
 * PDA for `market`/`outcomeIndex`. Live accounts come from cp-swap PoolState.
 *
 * @param connection - Solana RPC connection.
 * @param params - Swap parameters. See {@link AmmSwapParams}.
 * @returns BuildResult with an unsigned transaction ready for signing.
 */
export async function buildAmmSwap(
  connection: Connection,
  params: AmmSwapParams,
): Promise<BuildResult> {
  validateAmmSwapAmounts(params.amountIn, params.minAmountOut);
  if (
    !Number.isSafeInteger(params.outcomeIndex) ||
    params.outcomeIndex < 0 ||
    params.outcomeIndex > 255
  ) {
    throw new Error('Outcome index must be an unsigned byte');
  }
  const [outcomeMint] = deriveOutcomeMintPda(params.market, params.outcomeIndex);
  const pool = await fetchGraduatedPoolInfo(connection, {
    ammConfig: deriveAmmConfigPda(CP_SWAP_PROGRAM_ID),
    ammProgramId: CP_SWAP_PROGRAM_ID,
    quoteMint: params.collateralMint,
    outcomeMint,
  });
  const poolAccounts = {
    ...pool,
    token0Mint: pool.quoteTokenIndex === 0 ? pool.quoteMint : pool.outcomeMint,
    token1Mint: pool.quoteTokenIndex === 0 ? pool.outcomeMint : pool.quoteMint,
    observationKey: pool.observationState,
  };
  const inputMint = params.direction === 'buy' ? pool.quoteMint : pool.outcomeMint;
  const route = resolveAmmSwapRoute(poolAccounts, inputMint);
  const ataPayer = params.payer ?? params.user;
  // Idempotent ATA creation for both sides — the input ATA already holds the
  // user's balance; the output ATA may not exist yet (first buy/sell).
  const preInstructions = [
    createIdempotentAtaIx(ataPayer, params.user, inputMint, route.inputTokenProgram),
    createIdempotentAtaIx(ataPayer, params.user, route.outputMint, route.outputTokenProgram),
  ];

  const ix = await buildAmmSwapInstruction(connection, {
    pool: poolAccounts,
    user: params.user,
    inputMint,
    amountIn: params.amountIn,
    minAmountOut: params.minAmountOut,
  });

  return createBuildResult(
    [...preInstructions, ix],
    uniqueSignerKeys(params.user, ataPayer),
    params.computeUnits ?? estimateComputeUnits('amm_swap'),
  );
}
